import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { BRIGHTNESS_PRESETS, DEFAULT_PRESET, makeCurve, presetByKey } from './brightness.mjs';

const STORAGE_KEY = 'starguessr:brightness';
const PARAM = 'brightness';

/*
 * A switch for the star brightness curve, for judging by eye which one looks
 * like a night sky. The question is not answerable from the numbers — the whole
 * point is what it looks like — so the useful thing is to be able to flip
 * between curves over one unchanging sky and watch which stars come and go.
 *
 * Off unless the URL asks for it: /hardgame/?brightness. Ordinary play
 * never sees it, and the choice survives into the leaderboard-visible game only
 * as a rendering preference, never as anything that changes the astronomy or
 * the scoring.
 */
function tunerRequested() {
  try {
    return new URLSearchParams(window.location.search).has(PARAM);
  } catch {
    return false;
  }
}

function readInitial() {
  /*
   * Ordinary play always gets the shipped curve. Without this, a preset picked
   * once while tuning would sit in localStorage and quietly become that
   * browser's permanent sky — the tuner is a workbench, not a setting.
   */
  if (!tunerRequested()) return DEFAULT_PRESET;

  let fromUrl = null;
  try {
    fromUrl = new URLSearchParams(window.location.search).get(PARAM);
  } catch {
    fromUrl = null;
  }
  if (fromUrl && BRIGHTNESS_PRESETS.some((preset) => preset.key === fromUrl)) return fromUrl;

  try {
    const saved = window.localStorage.getItem(STORAGE_KEY);
    if (saved && BRIGHTNESS_PRESETS.some((preset) => preset.key === saved)) return saved;
  } catch {
    // Private windows and blocked site data both throw here; the default is fine.
  }
  return DEFAULT_PRESET;
}

export function useBrightnessCurve() {
  const [key, setKeyState] = useState(readInitial);
  const [enabled] = useState(tunerRequested);

  const setKey = useCallback((next) => {
    setKeyState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // Not being able to remember the choice is not a reason to refuse it.
    }
  }, []);

  const curve = useMemo(() => makeCurve(key), [key]);
  return { curve, presetKey: key, setPreset: setKey, enabled };
}

export default function BrightnessTuner({ presetKey, onChange }) {
  const preset = presetByKey(presetKey);

  // Number keys switch presets, so a comparison is one keystroke rather than a
  // hunt for the right button while the thing you are judging is off screen.
  useEffect(() => {
    const onKey = (event) => {
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      const index = Number(event.key) - 1;
      if (Number.isInteger(index) && index >= 0 && index < BRIGHTNESS_PRESETS.length) {
        onChange(BRIGHTNESS_PRESETS[index].key);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onChange]);

  return (
    <div className="sg-tuner">
      <p className="sg-tuner-head">Star brightness</p>
      <ol className="sg-tuner-list">
        {BRIGHTNESS_PRESETS.map((entry, index) => (
          <li key={entry.key}>
            <button
              type="button"
              className={entry.key === presetKey ? 'is-current' : ''}
              onClick={() => onChange(entry.key)}
            >
              <span className="sg-tuner-key">{index + 1}</span>
              {entry.label}
            </button>
          </li>
        ))}
      </ol>
      <p className="sg-tuner-note">{preset.note}</p>
      <p className="sg-tuner-numbers">
        gamma {preset.gamma} · floor {preset.alphaFloor} · limit{' '}
        {preset.magLimit >= 6.5 ? 'none' : preset.magLimit}
      </p>
    </div>
  );
}
